import React, { useEffect, useMemo, useState } from 'react';
import { SetpointHistoryItem, fetchSetpointHistory } from '../../../services/setpoints/SetpointHistoryService';
import './setpoint-history.scss';
import { useTable, useFilters, useSortBy, useGlobalFilter, Column, TableInstance, ColumnInstance, UseSortByColumnProps } from 'react-table';
import { DefaultColumnFilter } from '../../../components/data-tables/helpers/DefaultColumnFilter';
import { createMultiConditionFilter } from '../../../utilities/tableFilters';
import ExportTable from '../../../components/common/export-table/ExportTable';
import { formatDate } from '../../../utilities/dateHelpers';

interface SetpointHistoryProps {
    assetId?: string;
    parameterName?: string;
    rPermission: { [key: string]: boolean };
}

type ExtendedSetpointHistoryItem = SetpointHistoryItem & { prevValue?: string };

const SetpointHistory: React.FC<SetpointHistoryProps> = ({ assetId, parameterName, rPermission }) => {
    const [internalItems, setInternalItems] = useState<ExtendedSetpointHistoryItem[]>([]);
    const [internalLoading, setInternalLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const effectiveItems: ExtendedSetpointHistoryItem[] = internalItems;

    useEffect(() => {
        if (!assetId || !parameterName) return;
        setInternalLoading(true);
        setInternalItems([]);
        fetchSetpointHistory(assetId, parameterName)
            .then((data) => {
                const sorted = [...data].sort((a, b) => {
                    const timeA = a?.date ? new Date(a.date).getTime() : 0;
                    const timeB = b?.date ? new Date(b.date).getTime() : 0;
                    return timeB - timeA;
                });
                const withPrev: ExtendedSetpointHistoryItem[] = sorted.map((item, idx) => ({
                    ...item,
                    prevValue: idx < sorted.length - 1 ? sorted[idx + 1].value : undefined,
                }));
                setInternalItems(withPrev);
            })
            .catch(() => setInternalItems([]))
            .finally(() => setInternalLoading(false));
    }, [assetId, parameterName]);


    // Only pass dummy functions to createMultiConditionFilter so it does not update state
    const multiConditionFilter = useMemo(() => createMultiConditionFilter(() => null, { current: [] }), []);

    const columns: Column<ExtendedSetpointHistoryItem>[] = useMemo(
        () => [
            {
                Header: 'Date & time',
                accessor: 'date' as const,
                filter: multiConditionFilter,
                Cell: ({ value }: { value: string }) => <span>{formatDate(value, true)}</span>,
                sortType: (rowA: any, rowB: any, columnId: string) => {
                    const dateA = new Date(rowA.values[columnId]);
                    const dateB = new Date(rowB.values[columnId]);
                    if (dateA < dateB) return -1;
                    if (dateA > dateB) return 1;
                    return 0;
                }
            },
            {
                Header: 'User',
                accessor: 'user' as const,
                filter: multiConditionFilter,
            },
            {
                Header: 'Action',
                accessor: 'action' as const,
                filter: multiConditionFilter,
                Cell: ({ value }: { value?: string }) => <span>{value || '-'}</span>
            },
            {
                Header: 'Previous value',
                accessor: 'prevValue' as const,
                filter: multiConditionFilter,
                Cell: ({ value }: { value: string | null | undefined }) => <span>{value ?? '-'}</span>
            },
            {
                Header: 'New value',
                accessor: 'value' as const,
                filter: multiConditionFilter,
            },

        ],
        []
    );

    const clearFilterForColumn = () => {
        setAllFilters([]);
    };

    const defaultColumn: any = React.useMemo(
        () => ({
            Filter: (props: any) => (
                <DefaultColumnFilter
                    {...props}
                    clearFilterForColumn={clearFilterForColumn}
                    isFullscreen={true}
                />
            ),
        }),
        []
    );

    const instance = useTable<ExtendedSetpointHistoryItem>(
        {
            columns,
            data: effectiveItems,
            defaultColumn,
            getRowId: (_row: ExtendedSetpointHistoryItem, index: number) => `sp-${index}`,
        },
        useGlobalFilter,
        useFilters,
        useSortBy
    );

    const { getTableProps, headerGroups, rows, prepareRow, setAllFilters, setGlobalFilter } = instance as TableInstance<ExtendedSetpointHistoryItem> & {
        setAllFilters: (filters: any[]) => void;
        setGlobalFilter: (filterValue: any) => void;
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchTerm(value);
        setGlobalFilter(value || undefined);
    };

    const clearSearch = () => {
        setSearchTerm('');
        setGlobalFilter(undefined);
    };

    const exportColumns = [
        { key: 'date', header: 'Date & time', accessor: (row: any) => formatDate(row.values.date, true) || '' },
        { key: 'user', header: 'User', accessor: (row: any) => row.values.user || '' },
        { key: 'action', header: 'Action', accessor: (row: any) => row.values.action || '' },
        { key: 'prevValue', header: 'Previous value', accessor: (row: any) => row.values.prevValue || '' },
        { key: 'value', header: 'New value', accessor: (row: any) => row.values.value || '' },
    ];

    return (
        <div className='sp-history'>
            <div className='sp-history__header-row'>
                <ExportTable
                    data={rows}
                    fileName={`Setpoint-History-${parameterName || 'parameter'}`}
                    sheetName='Setpoint History'
                    columns={exportColumns}
                    disabled={internalLoading || rows.length === 0}
                    rPermission={rPermission}
                />
                <div className='sp-history__search-container'>
                    <div className='sp-history__search-box'>
                        <span className='ico-search' />
                        <div className='sp-history__search-input-wrapper'>
                            <input
                                type='text'
                                placeholder='Search'
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="sp-history__search-input"
                            />
                        </div>
                        {searchTerm && (
                            <button
                                className='sp-history__clear-button'
                                onClick={clearSearch}
                                type='button'
                                aria-label='Clear search'
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {internalLoading ? (
                <div className='sp-history__status'>Loading...</div>
            ) : effectiveItems.length === 0 ? (
                <div className='sp-history__status'>No history available.</div>
            ) : rows.length === 0 ? (
                <div className='sp-history__status'>No matching records found.</div>
            ) : (
                <div className='sp-history__table-wrapper'>
                    <table {...getTableProps()} className='sp-history__table'>
                        <thead>
                            {headerGroups.map((headerGroup) => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((col) => {
                                        const column = col as unknown as ColumnInstance<SetpointHistoryItem> & UseSortByColumnProps<SetpointHistoryItem> as any;
                                        return (
                                            <th
                                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                                className='sp-history__th'
                                            >
                                                <div className='sp-history__th-content'>
                                                    <span>{column.render('Header')}</span>
                                                    {column.isSorted && (
                                                        <span className={`tdt-sort-icon ${column.isSortedDesc ? 'desc' : 'asc'}`} />
                                                    )}
                                                    <span>{column.canFilter ? column.render('Filter') : null}</span>
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()} className='sp-history__row'>
                                        {row.cells.map((cell) => (
                                            <td {...cell.getCellProps()} className='sp-history__td'>
                                                {cell.value ? cell.render('Cell') : '-'}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SetpointHistory;
